import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";

export const LicensesGrid = ({
  selectedDuplicated,
  selectedCounties,
  primaryStatus,
  secondaryStatus,
  selectedLicenses,
  setTotalLicenses,
  setTotalDuplicatedAddress,
  setTotalSingleAddress,
  title = "DBPR Contractors",
  tasksDataRows,
  setTasksData,
  isSm,
  page,
  setPage,
  setRowsPerPage,
  totalRowsCount,
  setTotalRowsCount,
  rowsPerPage,
  sortModel,
  setSortModel,
  filterModel,
  setFilterModel,
  setPivot,
  setHeaders,
  onExport,
  exportLoading,
  first,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const [errorText, setErrorText] = useState("");
  const [gridLoading, setGridLoading] = useState(false);

  useEffect(() => {
    const clearTextInterval = setInterval(() => {
      if (errorText !== "") {
        setErrorText("");
      }
    }, 3000);

    return () => {
      clearInterval(clearTextInterval); // Clear the interval when the component unmounts
    };
  }, [errorText]);

  const handleSortModelChange = (model, details) => {
    setSortModel(model);
    fetchData(page, rowsPerPage, model, filterModel);
  };

  const handlePaginationModelChange = (model, details) => {
    const newPage = model.page;
    const newRowsPerPage = model.pageSize;
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
    fetchData(newPage, newRowsPerPage, sortModel, filterModel);
  };

  const handleFilterModelChange = (model, details) => {
    setFilterModel(model);
    fetchData(page, rowsPerPage, sortModel, model);
  };

  const fetchData = async (newPage, newRowsPerPage, sortModel, filterModel) => {
    setGridLoading(true);
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/filtered_licenses`,
        {
          page: newPage,
          rowsPerPage: newRowsPerPage,
          sortModel: sortModel,
          filterModel: filterModel,
          selectedLicenses: selectedLicenses,
          counties: selectedCounties,
          statuses: {
            primary: primaryStatus,
            secondary: secondaryStatus,
          },
          duplicated: selectedDuplicated,
          exportPivot: false,
          export_csv: false,
          currentSession: currentSession,
          currentPath: window.location.pathname,
        },
        {
          withCredentials: true,
        },
      );

      setTasksData(resp.data.data.contractors);
      setTotalRowsCount(resp.data.data.totals.total_licenses);
      setTotalLicenses(resp.data.data.totals.total_licenses);
      setTotalDuplicatedAddress(
        resp.data.data.totals.licenses_with_duplicated_addresses,
      );
      setTotalSingleAddress(resp.data.data.totals.total_license_single_address);
      const headersResp = Object.keys(resp.data.data.pivot[0]);
      setPivot(resp.data.data.pivot);
      setHeaders(headersResp);
      setGridLoading(false);
    } catch (e) {
      setGridLoading(false);
    }
  };

  const licenseDataColumns = [
    // {
    //   field: "id",
    //   headerName: "ID",
    //   width: isSm ? 180 : 180,
    // },
    {
      field: "primary_name",
      headerName: "Primary Name",
      width: isSm ? 170 : 250,
      filterable: true,
    },
    {
      field: "dba_name",
      headerName: "DBA Name",
      width: isSm ? 180 : 350,
      filterable: true,
    },
    {
      field: "address",
      headerName: "Address",
      width: isSm ? 160 : 350,
      filterable: true,
    },
    {
      field: "county",
      headerName: "County",
      width: isSm ? 180 : 180,
      filterable: true,
    },
    {
      field: "license_type",
      headerName: "License Type",
      width: isSm ? 130 : 300,
      filterable: true,
    },
    {
      field: "rank",
      headerName: "Rank",
      width: isSm ? 130 : 180,
      filterable: true,
    },
    {
      field: "license_number",
      headerName: "License Number",
      width: isSm ? 130 : 180,
      filterable: true,
    },
    {
      field: "status_primary",
      headerName: "Primary Status",
      width: isSm ? 130 : 180,
      renderCell: (params) => {
        return params?.row?.status?.primary || "—";
      },
    },
    {
      field: "status_secondary",
      headerName: "Secondary Status",
      width: isSm ? 130 : 180,
      renderCell: (params) => {
        return params?.row?.status?.secondary || "—";
      },
    },
    {
      field: "issued_at",
      headerName: "Issued Date",
      width: 150,
      renderCell: (params) => {
        return params?.row?.reg_date?.original || "—";
      },
    },
    {
      field: "eff_date",
      headerName: "Renewed Date",
      width: 150,
      renderCell: (params) => {
        return params?.row?.reg_date?.effective || "—";
      },
    },
    {
      field: "exp_date",
      headerName: "Expiration Date",
      width: 150,
      renderCell: (params) => {
        return params?.row?.reg_date?.expiration || "—";
      },
    },
  ];

  return (
    <Card sx={{ minHeight: "35rem", position: "relative" }}>
      <CardHeader title={title} />
      {onExport && (
        <Tooltip
          sx={{ position: "absolute", top: 28, right: 15 }}
          title="Export Contractors to CSV"
        >
          <IconButton
            disabled={exportLoading || !first}
            color="primary"
            onClick={onExport}
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      )}
      <CardContent>
        <DataGrid
          className="cell"
          sx={{
            "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            minHeight: "24rem",
          }}
          rows={tasksDataRows ? tasksDataRows : []}
          columns={licenseDataColumns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 5,
              },
            },
          }}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          // disableRowSelectionOnClick
          rowCount={totalRowsCount}
          pageSize={rowsPerPage}
          paginationMode="server"
          paginationModel={{
            page: page, // Set the current page value here
            pageSize: rowsPerPage, // Set the page size value here
          }}
          onPaginationModelChange={(model, details) => {
            handlePaginationModelChange(model, details);
          }}
          onFilterModelChange={(model, details) => {
            handleFilterModelChange(model, details);
          }}
          onSortModelChange={(model, details) => {
            handleSortModelChange(model, details);
          }}
          // onRowClick={(params, event, details) => {
          //   handleSaveRow(params, event, details);
          // }}
          loading={gridLoading}
        />

        <Typography
          variant="body1"
          color="#F2CD00"
          fontWeight="bold"
          textAlign="center"
          sx={{ mt: "1rem", minHeight: "1.5rem" }}
        >
          {errorText}
        </Typography>
      </CardContent>
    </Card>
  );
};
